import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A utility hook to debounce a function. The return is the function, use for advanced cases where the usage of the
 * function directly is required. Use `useDebouncedState` instead for simple state management on a debounced interval
 * @param callback
 * @param timeout
 * @returns The debounced callback
 */
export function useDebounce<T extends unknown[]>(callback: (...a: T) => void, timeout: number) {
  const ref = useRef<(...args: T) => void>();

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  // Disabling exhaustive deps because this eslint rule gets confused when the
  // function passed to `useCallback` is not inlined.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallback = useCallback(
    debounce((...args: T) => {
      ref.current?.(...args);
    }, timeout),
    []
  );

  return debouncedCallback;
}

/**
 * A hook returning a stateful value, and a setter to update it, on a debounced interval. Upon invoking the setter, the
 * stateful value will be updated immediately, and the callback invoked after the timeout has elapsed.
 * @param initialValue
 * @param callback
 * @param timeout
 * @returns [state, setState]
 */
export function useDebouncedState<T>(
  initialValue: T,
  callback: (newValue: T) => void,
  timeout: number
): [value: T, debouncedSetValue: (newValue: T) => void] {
  const [value, setValue] = useState(initialValue);
  const debouncedSetValue = useDebounce((newValue: T) => callback(newValue), timeout);

  const setValueAndDebounceCallback = useCallback(
    (newValue: T) => {
      setValue(newValue);
      debouncedSetValue(newValue);
    },
    [setValue, debouncedSetValue]
  );

  return [value, setValueAndDebounceCallback];
}
